// Closing the loop on a suggestion.
//
// ── THE DEFECT THIS EXISTS TO FIX ─────────────────────────────────────────
// The suggestions inbox let an admin set a status and type a response into a
// box labelled "visible to nobody but you for now". So the entire mechanism was
// an admin talking to themselves. Someone submits an idea, it is classified, and
// they never hear anything.
//
// A suggestion box that never answers is worse than no suggestion box. It asks
// people to spend effort, teaches them it goes nowhere, and the second lesson
// generalises to every other channel Scenta opens.
//
// ── WHAT ACTUALLY CLOSES A LOOP ───────────────────────────────────────────
// Not a status. A status is what the ADMIN knows. Closing the loop means the
// person who submitted it learns what happened and why. Those are different
// events and this module keeps them separate, because collapsing them is exactly
// how the current version convinced itself it was working.
//
// ── ANONYMITY IS THE HARD CASE, AND IT IS THE COMMON ONE ──────────────────
// The submit form defaults to anonymous. An anonymous suggestion has nobody to
// notify — so the ONLY way to close its loop is to answer it somewhere everyone
// can see. That is a genuinely different obligation, not a lesser one, and a
// system that treats "no author to notify" as "nothing to do" silently discards
// the majority of its input.
//
// Pure. The clock is supplied.

export const SUGGESTION_LOOP_VERSION = "suggestion-loop-v0.1";

export type SuggestionStatus =
  | "new" | "reviewed" | "awaiting_author" | "planned" | "implemented" | "wont_do";

/** Statuses that represent a decision having been made. */
export const DECIDED: readonly SuggestionStatus[] = ["planned", "implemented", "wont_do"];

export const STATUS_LABEL: Record<SuggestionStatus, string> = {
  new: "New",
  reviewed: "Read, not yet decided",
  awaiting_author: "Waiting on the person who asked",
  planned: "Going to do it",
  implemented: "Done",
  wont_do: "Not doing it",
};

/** How long a suggestion may sit unacknowledged before that is a problem. */
export const ACKNOWLEDGE_DAYS = 3;
/** How long it may sit read-but-undecided. */
export const DECIDE_DAYS = 14;

export interface SuggestionFacts {
  id: string;
  content: string;
  category: string | null;
  is_anonymous: boolean;
  author_id: string | null;
  status: SuggestionStatus;
  /** The admin's written answer. Null means none written. */
  admin_response: string | null;
  /** Whether that answer has been published where the submitter can read it. */
  response_published: boolean;
  /**
   * The admin's restatement of the suggestion, for publishing.
   *
   * Anonymous content is not republished verbatim. Someone who writes "my
   * manager keeps scheduling me through my daughter's appointments" chose
   * anonymity, and posting those words company-wide identifies them as surely
   * as a name would. So publishing an anonymous suggestion means publishing a
   * restatement of the ASK, not the submission.
   */
  public_summary: string | null;
  created_at: string;
  acknowledged_at: string | null;
  decided_at: string | null;
}

export type LoopState =
  | "unread"
  | "read_undecided"
  | "decided_unanswered"
  | "answered_privately"
  | "closed";

export const LOOP_STATE_TEXT: Record<LoopState, string> = {
  unread: "Nobody has looked at this yet.",
  read_undecided: "Read, but no decision has been made.",
  decided_unanswered: "Decided, but the person who submitted it has not been told anything.",
  answered_privately: "An answer was written but never published, so it has reached nobody.",
  closed: "Answered, and the answer is where the submitter can read it.",
};

export interface SuggestionLoop {
  id: string;
  status: SuggestionStatus;
  status_label: string;
  loop: LoopState;
  loop_text: string;
  /** True when the person who submitted it now knows the outcome. */
  loop_closed: boolean;
  /** Days since submission. */
  age_days: number;
  /** True when this has waited longer than the relevant promise. */
  overdue: boolean;
  /** The one thing to do next, or null when nothing is owed. */
  next_action: string | null;
  /**
   * How an answer can reach this submitter at all.
   *
   * Anonymous suggestions have no recipient, so publishing is the only route.
   * Naming this per suggestion stops "we cannot notify them" from becoming
   * "there is nothing to do".
   */
  answer_route: "notify_author" | "publish_publicly";
}

const daysSince = (nowMs: number, iso: string | null): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.floor((nowMs - t) / 86_400_000);
};

const hasText = (s: string | null) => !!s && s.trim().length > 0;

export function loopStateOf(f: SuggestionFacts): LoopState {
  const decided = DECIDED.includes(f.status);

  if (decided) {
    if (!hasText(f.admin_response)) return "decided_unanswered";
    return f.response_published ? "closed" : "answered_privately";
  }
  return f.status === "new" ? "unread" : "read_undecided";
}

/**
 * One suggestion, with what is owed on it.
 *
 * `overdue` is measured against different promises at different stages —
 * acknowledging is quick, deciding takes longer — because a single deadline
 * would either rush every decision or excuse every silence.
 */
export function suggestionLoop(f: SuggestionFacts, nowMs: number): SuggestionLoop {
  const loop = loopStateOf(f);
  const age = daysSince(nowMs, f.created_at) ?? 0;
  const route: SuggestionLoop["answer_route"] =
    f.is_anonymous || !f.author_id ? "publish_publicly" : "notify_author";

  const overdue =
    loop === "unread" ? age > ACKNOWLEDGE_DAYS
      : loop === "closed" ? false
        : age > DECIDE_DAYS;

  const nextAction =
    loop === "unread" ? "Read it and mark it read."
      : loop === "read_undecided" ? "Decide: planned, done, or not doing it."
        : loop === "decided_unanswered"
          ? (f.status === "wont_do"
            // The one people actually remember. A refusal without a reason is
            // what teaches everyone else not to bother submitting.
            ? "Write why not. A decision with no reason is what stops the next suggestion."
            : "Write what is happening and when.")
          : loop === "answered_privately"
            ? (route === "publish_publicly"
              ? (hasText(f.public_summary)
                ? "Publish the answer — this was submitted anonymously, so there is nobody to notify."
                : "Restate the ask in your own words, then publish. This was submitted anonymously, so "
                  + "there is nobody to notify and the original wording could identify them.")
              : "Publish the answer so the person who submitted it can read it.")
            : null;

  return {
    id: f.id,
    status: f.status,
    status_label: STATUS_LABEL[f.status],
    loop,
    loop_text: LOOP_STATE_TEXT[loop],
    loop_closed: loop === "closed",
    age_days: age,
    overdue,
    next_action: nextAction,
    answer_route: route,
  };
}

// ── guarding the status change ─────────────────────────────────────────────

export type StatusChangeVerdict = { allowed: true } | { allowed: false; reason: string };

/**
 * May this status change be saved?
 *
 * ── WHY A DECISION REQUIRES A REASON ────────────────────────────────────
 * Setting `wont_do` with an empty response is the single move that kills a
 * suggestion box. The submitter sees a refusal with no explanation, concludes
 * the box is decorative, and stops using it — and tells other people.
 *
 * `planned` and `implemented` require one too, for a smaller reason: "planned"
 * with no detail is indistinguishable from a polite no.
 */
export type PublishVerdict = { allowed: true } | { allowed: false; reason: string };

/**
 * May this answer be published?
 *
 * The guard exists for anonymous suggestions specifically. Publishing raw
 * anonymous content is how a suggestion box becomes a way to accidentally out
 * the person who used it, and the harm is not undoable once it is posted.
 */
export function publishVerdict(f: Pick<SuggestionFacts,
  "is_anonymous" | "author_id" | "admin_response" | "public_summary">): PublishVerdict {
  if (!hasText(f.admin_response)) {
    return { allowed: false, reason: "There is no answer to publish yet." };
  }

  const anonymous = f.is_anonymous || !f.author_id;
  if (anonymous && !hasText(f.public_summary)) {
    return {
      allowed: false,
      reason: "Write a short restatement of the ask before publishing. This was submitted anonymously, "
        + "and posting the original wording can identify the person who wrote it.",
    };
  }
  return { allowed: true };
}

export function statusChangeVerdict(input: {
  next: SuggestionStatus;
  admin_response: string | null;
}): StatusChangeVerdict {
  if (!DECIDED.includes(input.next)) return { allowed: true };

  if (!hasText(input.admin_response)) {
    return {
      allowed: false,
      reason: input.next === "wont_do"
        ? "Say why not before saving. A refusal with no reason is what stops the next suggestion."
        : "Write what is happening before saving. A decision with no detail reads as a polite no.",
    };
  }
  return { allowed: true };
}

// ── the inbox ──────────────────────────────────────────────────────────────

export interface SuggestionInbox {
  rows: SuggestionLoop[];
  /** Loops still open, which is the only count that matters. */
  open: number;
  /** Open past their promise. */
  overdue: number;
  /** Decided but never communicated — the failure mode that looks like success. */
  decided_but_silent: number;
  /** Anonymous suggestions awaiting a public answer. */
  awaiting_publication: number;
  headline: string;
}

/**
 * The inbox, ordered by what is owed rather than by date.
 *
 * A date-ordered inbox buries the six-week-old refusal nobody explained under
 * this morning's arrivals, which is how a backlog becomes permanent.
 */
export function suggestionInbox(all: SuggestionFacts[], nowMs: number): SuggestionInbox {
  const rows = all.map(f => suggestionLoop(f, nowMs));

  const ORDER: Record<LoopState, number> = {
    decided_unanswered: 0,
    answered_privately: 1,
    unread: 2,
    read_undecided: 3,
    closed: 4,
  };
  rows.sort((a, b) => ORDER[a.loop] - ORDER[b.loop] || b.age_days - a.age_days);

  const open = rows.filter(r => !r.loop_closed).length;
  const overdue = rows.filter(r => r.overdue).length;
  const silent = rows.filter(r => r.loop === "decided_unanswered" || r.loop === "answered_privately").length;
  const awaiting = rows.filter(r =>
    r.loop === "answered_privately" && r.answer_route === "publish_publicly").length;

  const parts: string[] = [];
  if (!rows.length) parts.push("No suggestions yet.");
  else if (!open) parts.push(`All ${rows.length} suggestions have been answered.`);
  else {
    parts.push(`${open} open`);
    // Stated first because it is the failure that looks like success: the
    // status column says "implemented" and the submitter still knows nothing.
    if (silent) parts.push(`${silent} decided but never communicated`);
    if (overdue) parts.push(`${overdue} past its promise`);
  }

  return {
    rows, open, overdue,
    decided_but_silent: silent,
    awaiting_publication: awaiting,
    headline: parts.join(" · "),
  };
}
